import { createRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { authedLayoutRoute } from './authed.js';
import {
  approveAdvance,
  confirmDisbursement,
  listAdvanceRequests,
  rejectAdvance,
  type AdvanceRequest,
} from '../api/advances.js';
import type { CurrencyCode } from '@kora/types';
import { formatDate, formatMoney } from '../lib/format.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

/**
 * /loans/requests — org-wide queue of employee loan applications (ADR-0009
 * D11, PR ⑦d/⑦e). Shows applications awaiting review (`requested`) AND approved
 * loans awaiting disbursement (`pending`) so the whole review → disburse flow
 * lives in one place. HR approves/rejects requested rows and disburses pending
 * rows. RBAC enforced by the API (owner|admin|hr).
 */
export const loansRequestsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/loans/requests',
  component: LoanRequestsPage,
});

function LoanRequestsPage(): JSX.Element {
  const qc = useQueryClient();
  const requested = useQuery({
    queryKey: ['advance-requests', 'requested'],
    queryFn: () => listAdvanceRequests('requested'),
  });
  const pending = useQuery({
    queryKey: ['advance-requests', 'pending'],
    queryFn: () => listAdvanceRequests('pending'),
  });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['advance-requests'] });
    void qc.invalidateQueries({ queryKey: ['advances'] });
  };

  const isLoading = requested.isLoading || pending.isLoading;
  const isError = requested.isError || pending.isError;
  const error = requested.error ?? pending.error;
  const rows = [...(requested.data?.requests ?? []), ...(pending.data?.requests ?? [])];

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header>
        <h1 className="font-serif text-3xl">Loan requests</h1>
        <p className="text-sm text-gray-400">Applications awaiting review, and approved loans awaiting disbursement.</p>
      </header>

      {isLoading ? <p className="text-gray-500">Loading...</p> : null}
      {isError ? (
        <Alert variant="error">
          {error instanceof ApiError ? error.message : 'Could not load requests.'}
        </Alert>
      ) : null}
      {!isLoading && !isError && rows.length === 0 ? (
        <div className="rounded border border-dashed border-gray-800 p-10 text-center text-gray-400">No loan requests or pending disbursements.</div>
      ) : null}

      <div className="space-y-3">
        {rows.map((r) => (
          <RequestRow key={r.id} request={r} onChanged={invalidate} />
        ))}
      </div>
    </section>
  );
}

function RequestRow({ request, onChanged }: { request: AdvanceRequest; onChanged: () => void }): JSX.Element {
  const [mode, setMode] = useState<'none' | 'approve' | 'reject'>('none');
  const [disburseErr, setDisburseErr] = useState<string | null>(null);
  const name = request.employee
    ? `${request.employee.firstName} ${request.employee.lastName} · ${request.employee.employeeNumber}`
    : request.employeeId;
  const isPending = request.status === 'pending';

  const disburse = useMutation({
    mutationFn: () => confirmDisbursement(request.id),
    onSuccess: onChanged,
    onError: (e) => setDisburseErr(e instanceof ApiError ? e.message : 'Disbursement failed.'),
  });

  return (
    <div className="rounded border border-gray-900 p-4 space-y-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-white">{name}</span>
            <span className="text-[0.65rem] uppercase tracking-wide rounded px-1.5 py-0.5 border border-gray-700 text-gray-300">
              {isPending ? 'awaiting disbursement' : 'requested'}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            {formatMoney(request.principalAmount, request.currency as CurrencyCode)} · {request.kind.replace('_', ' ')}
            {request.requestedTermMonths ? ` · ${request.requestedTermMonths} mo` : ''} ·{' '}
            {isPending
              ? `approved ${request.approvedAt ? formatDate(request.approvedAt) : '—'}`
              : `requested ${request.requestedAt ? formatDate(request.requestedAt) : '—'}`}
          </div>
          {request.requestPurpose ? <div className="text-xs text-gray-400 mt-1">“{request.requestPurpose}”</div> : null}
        </div>
        {request.employee ? (
          <Link to="/employees/$id/advances" params={{ id: request.employee.id }} search={{ mode: 'new' as const }} className="text-xs text-gray-500 hover:underline whitespace-nowrap">
            View employee
          </Link>
        ) : null}
      </div>

      {!isPending && mode === 'none' ? (
        <div className="flex gap-2">
          <button type="button" onClick={() => setMode('approve')} className="text-xs border border-gray-700 rounded px-3 py-1 hover:bg-gray-900">Approve…</button>
          <button type="button" onClick={() => setMode('reject')} className="text-xs border border-red-800 text-red-400 rounded px-3 py-1 hover:bg-red-900/40">Reject…</button>
        </div>
      ) : null}

      {isPending ? (
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => { setDisburseErr(null); disburse.mutate(); }}
            disabled={disburse.isPending}
            className="text-xs rounded bg-white text-black px-3 py-1 font-medium hover:bg-gray-200 disabled:opacity-50"
          >
            {disburse.isPending ? 'Disbursing…' : 'Confirm disbursement'}
          </button>
          {disburseErr ? <Alert variant="error" compact>{disburseErr}</Alert> : null}
        </div>
      ) : null}

      {mode === 'approve' ? (
        <ApproveForm request={request} onDone={() => { setMode('none'); onChanged(); }} onCancel={() => setMode('none')} />
      ) : null}
      {mode === 'reject' ? (
        <RejectForm requestId={request.id} onDone={() => { setMode('none'); onChanged(); }} onCancel={() => setMode('none')} />
      ) : null}
    </div>
  );
}

/** Minimal shape ApproveForm needs — works for both the queue (AdvanceRequest)
 *  and the per-employee page (AdvanceResponse). */
export interface ApprovableAdvance {
  id: string;
  principalAmount: string;
  monthlyDeduction: string;
  requestedTermMonths?: number | null;
}

export function ApproveForm({ request, onDone, onCancel }: { request: ApprovableAdvance; onDone: () => void; onCancel: () => void }): JSX.Element {
  const [adjustedPrincipal, setPrincipal] = useState(request.principalAmount);
  const [adjustedMonthlyDeduction, setMonthly] = useState(request.monthlyDeduction);
  const [adjustedTermMonths, setTerm] = useState(request.requestedTermMonths ? String(request.requestedTermMonths) : '');
  const [err, setErr] = useState<string | null>(null);

  const approve = useMutation({
    mutationFn: () =>
      approveAdvance(request.id, {
        adjustedPrincipal,
        adjustedMonthlyDeduction,
        ...(adjustedTermMonths ? { adjustedTermMonths: Number(adjustedTermMonths) } : {}),
      }),
    onSuccess: onDone,
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Approve failed.'),
  });

  return (
    <form className="rounded border border-gray-800 bg-gray-950/60 p-3 space-y-3" onSubmit={(e) => { e.preventDefault(); setErr(null); approve.mutate(); }}>
      <p className="text-xs text-gray-400">Adjust the terms if needed, then approve. This moves the loan to <span className="text-gray-200">pending</span> disbursement.</p>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Principal"><input aria-label="Approved principal" value={adjustedPrincipal} onChange={(e) => setPrincipal(e.target.value)} className="rounded border border-gray-800 bg-gray-950 px-2 py-1 text-sm text-gray-200" /></Field>
        <Field label="Monthly deduction"><input aria-label="Approved monthly deduction" value={adjustedMonthlyDeduction} onChange={(e) => setMonthly(e.target.value)} className="rounded border border-gray-800 bg-gray-950 px-2 py-1 text-sm text-gray-200" /></Field>
        <Field label="Term (months)"><input aria-label="Approved term months" type="number" min="1" value={adjustedTermMonths} onChange={(e) => setTerm(e.target.value)} className="rounded border border-gray-800 bg-gray-950 px-2 py-1 text-sm text-gray-200" /></Field>
      </div>
      {err ? <Alert variant="error" compact>{err}</Alert> : null}
      <div className="flex gap-2">
        <button type="submit" disabled={approve.isPending} className="rounded bg-white text-black px-3 py-1 text-xs font-medium disabled:opacity-50">{approve.isPending ? 'Approving…' : 'Approve'}</button>
        <button type="button" onClick={onCancel} className="rounded border border-gray-700 px-3 py-1 text-xs text-gray-400 hover:bg-gray-900">Cancel</button>
      </div>
    </form>
  );
}

export function RejectForm({ requestId, onDone, onCancel }: { requestId: string; onDone: () => void; onCancel: () => void }): JSX.Element {
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const reject = useMutation({
    mutationFn: () => rejectAdvance(requestId, reason),
    onSuccess: onDone,
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Reject failed.'),
  });
  return (
    <form className="rounded border border-red-900/60 bg-red-950/20 p-3 space-y-3" onSubmit={(e) => { e.preventDefault(); setErr(null); reject.mutate(); }}>
      <Field label="Reason (shown to the employee)">
        <textarea aria-label="Rejection reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="rounded border border-gray-800 bg-gray-950 px-2 py-1 text-sm text-gray-200" />
      </Field>
      {err ? <Alert variant="error" compact>{err}</Alert> : null}
      <div className="flex gap-2">
        <button type="submit" disabled={reject.isPending || reason.trim().length === 0} className="rounded bg-red-600 text-white px-3 py-1 text-xs font-medium disabled:opacity-50">{reject.isPending ? 'Rejecting…' : 'Reject'}</button>
        <button type="button" onClick={onCancel} className="rounded border border-gray-700 px-3 py-1 text-xs text-gray-400 hover:bg-gray-900">Cancel</button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-xs text-gray-400">
      {label}
      {children}
    </label>
  );
}
